if (!window.SubcolCarousel) {
  window.SubcolCarousel = class {
    constructor(el) {
      this.el = el;
      this.id = el.dataset.carousel;
      this.track = el.querySelector('.carousel-track');
      this.viewport = el.querySelector('.carousel-viewport');
      this.prevBtn = el.querySelector('[data-dir="prev"]');
      this.nextBtn = el.querySelector('[data-dir="next"]');
      this.total = parseInt(el.dataset.total);
      this.desktopCols = parseInt(el.dataset.desktopColumns);
      this.mobileCols = parseInt(el.dataset.mobileColumns);
      this.currentIndex = 0;
      this.isInitialized = false;
      this.cachedItemWidth = null;
      this.cachedGap = null;
      this.resizeTimer = null;
      this.boundHandleResize = this.handleResize.bind(this);
      this.autoScrollEnabled = el.dataset.autoScroll === 'true';
      this.autoScrollInterval = parseInt(el.dataset.autoScrollInterval, 10) || 4000;
      this.autoScrollTimer = null;
      this.autoScrollResumeTimer = null;
      this.boundHandleVisibility = this.handleVisibilityChange.bind(this);
      this.resizeObserver = null;
      this.resizeSuspended = false;
      requestAnimationFrame(() => this.init());
    }

    init() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.cacheLayoutMeasurements();
      this.updateView();
      this.bindEvents();
      this.startAutoScroll();
      this.observeCardResize();
    }

    observeCardResize() {
      if (!this.track || !this.track.children[0] || !window.ResizeObserver) return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeSuspended) return;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (this.resizeSuspended) return;
          this.cacheLayoutMeasurements();
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
          this.updateView();
        }, 100);
      });
      this.resizeObserver.observe(this.track.children[0]);
    }

    cacheLayoutMeasurements() {
      if (this.track && this.track.children[0]) {
        this.cachedItemWidth = this.track.children[0].offsetWidth;
        this.cachedGap = parseInt(getComputedStyle(this.track).gap) || 0;
      }
    }

    get visibleItems() {
      return window.innerWidth >= 750 ? this.desktopCols : this.mobileCols;
    }

    get maxIndex() {
      return Math.max(0, this.total - this.visibleItems);
    }

    updateView() {
      if (!this.track || !this.cachedItemWidth) return;
      const offset = this.currentIndex * (this.cachedItemWidth + this.cachedGap);
      this.track.style.transform = `translateX(-${offset}px)`;
      if (this.viewport) this.viewport.scrollLeft = 0;
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateView();
      }
      this.pauseAutoScroll();
    }

    next() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
        this.updateView();
      }
      this.pauseAutoScroll();
    }

    autoNext() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
      } else {
        this.currentIndex = 0;
      }
      this.updateView();
    }

    startAutoScroll() {
      if (!this.autoScrollEnabled) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = setInterval(() => this.autoNext(), this.autoScrollInterval);
    }

    stopAutoScroll() {
      clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
      clearTimeout(this.autoScrollResumeTimer);
    }

    pauseAutoScroll() {
      if (!this.autoScrollEnabled) return;
      this.stopAutoScroll();
      clearTimeout(this.autoScrollResumeTimer);
      this.autoScrollResumeTimer = setTimeout(() => this.startAutoScroll(), this.autoScrollInterval);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoScroll();
      } else {
        this.startAutoScroll();
      }
    }

    handleResize() {
      if (this.resizeSuspended) return;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.resizeSuspended) return;
        this.cacheLayoutMeasurements();
        this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
        this.updateView();
      }, 150);
    }

    suspendResize() {
      this.resizeSuspended = true;
      clearTimeout(this.resizeTimer);
    }

    resumeResize() {
      this.resizeSuspended = false;
      this.cacheLayoutMeasurements();
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      this.updateView();
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      let startX = 0;
      let isDragging = false;

      const handleStart = (e) => {
        startX = e.touches ? e.touches[0].clientX : e.clientX;
        isDragging = true;
      };

      const handleEnd = (e) => {
        if (!isDragging) return;
        const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const diff = startX - endX;

        if (Math.abs(diff) > 50) {
          diff > 0 ? this.next() : this.prev();
        }
        isDragging = false;
      };

      this.viewport.addEventListener('touchstart', handleStart, { passive: true });
      this.viewport.addEventListener('touchend', handleEnd, { passive: true });
      this.viewport.addEventListener('mousedown', handleStart);
      this.viewport.addEventListener('mouseup', handleEnd);
      this.viewport.addEventListener('mouseleave', () => { isDragging = false; });

      if (this.autoScrollEnabled) {
        this.viewport.addEventListener('mouseenter', () => this.stopAutoScroll());
        this.viewport.addEventListener('mouseleave', () => this.startAutoScroll());
        this.viewport.addEventListener('touchstart', () => this.pauseAutoScroll(), { passive: true });
        document.addEventListener('visibilitychange', this.boundHandleVisibility);
      }

      window.addEventListener('resize', this.boundHandleResize);
    }

    destroy() {
      window.removeEventListener('resize', this.boundHandleResize);
      document.removeEventListener('visibilitychange', this.boundHandleVisibility);
      clearTimeout(this.resizeTimer);
      this.stopAutoScroll();
      clearTimeout(this.autoScrollResumeTimer);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.isInitialized = false;
    }
  };

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(el => {
      if (!el.dataset.initialized) {
        const instance = new SubcolCarousel(el);
        el.__instance = instance;
        el.dataset.initialized = 'true';
      }
    });
  }

  document.addEventListener('subcol:video-modal-open', () => {
    document.querySelectorAll('[data-carousel]').forEach(el => {
      if (el.__instance) {
        el.__instance.suspendResize();
        el.__instance.stopAutoScroll();
      }
    });
  });

  document.addEventListener('subcol:video-modal-close', () => {
    document.querySelectorAll('[data-carousel]').forEach(el => {
      if (el.__instance) {
        el.__instance.resumeResize();
        el.__instance.startAutoScroll();
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  document.addEventListener('shopify:section:load', initCarousels);
  document.addEventListener('shopify:section:unload', (e) => {
    const carousel = e.target.querySelector('[data-carousel]');
    if (carousel && carousel.__instance) {
      carousel.__instance.destroy();
      delete carousel.__instance;
      delete carousel.dataset.initialized;
    }
  });
}

if (!window.SubcolVideoModal) {
  window.SubcolVideoModal = (function () {
    const ICON_CLOSE = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    const ICON_SHARE = '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';
    const ICON_PLAY = '<svg viewBox="0 0 24 24" fill="white" width="64" height="64"><path d="M8 5v14l11-7z"/></svg>';
    const ICON_PAUSE = '<svg viewBox="0 0 24 24" fill="white" width="64" height="64"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
    const ICON_MUTED = '<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>';
    const ICON_UNMUTED = '<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';
    const ICON_VIEW_MORE = '<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"></path></svg>';
    const ICON_CHEVRON_UP = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>';
    const ICON_CHEVRON_DOWN = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';

    let modal, video, closeBtn, viewMoreBtn, muteBtn, muteIcon, shareBtn, cardEl, titleEl, badgeEl, shopBtn, flashEl, videoWrap, navUpBtn, navDownBtn, endCardEl, endBtn;
    let lastFocusedTrigger = null;
    let triggerList = [];
    let triggerIndex = -1;
    let showingEndCard = false;

    function build() {
      modal = document.createElement('div');
      modal.className = 'subcol-video-modal';
      modal.innerHTML = `
        <div class="subcol-video-modal__panel">
          <div class="subcol-video-modal__video-wrap">
            <video class="subcol-video-modal__video" playsinline webkit-playsinline></video>
            <div class="subcol-video-modal__play-flash"></div>
          </div>
          <div class="subcol-video-modal__topbar">
            <button type="button" class="subcol-video-modal__close" aria-label="Close">${ICON_CLOSE}</button>
          </div>
          <div class="subcol-video-modal__nav">
            <button type="button" class="subcol-video-modal__nav-btn" data-nav="up" aria-label="Previous video">${ICON_CHEVRON_UP}</button>
            <button type="button" class="subcol-video-modal__nav-btn" data-nav="down" aria-label="Next video">${ICON_CHEVRON_DOWN}</button>
          </div>
          <div class="subcol-video-modal__actions">
            <button type="button" class="subcol-video-modal__action-btn" data-action="view-more" aria-label="View more reels" hidden>
              <span class="subcol-video-modal__action-icon">${ICON_VIEW_MORE}</span>
              <span>Reels</span>
            </button>
            <button type="button" class="subcol-video-modal__action-btn" data-action="mute" aria-label="Toggle mute">
              <span class="subcol-video-modal__action-icon">${ICON_UNMUTED}</span>
              <span>Mute</span>
            </button>
            <button type="button" class="subcol-video-modal__action-btn" data-action="share" aria-label="Share">
              <span class="subcol-video-modal__action-icon">${ICON_SHARE}</span>
              <span>Share</span>
            </button>
          </div>
          <div class="subcol-video-modal__card">
            <div class="subcol-video-modal__card-info">
              <p class="subcol-video-modal__title"></p>
              <span class="subcol-video-modal__badge" hidden></span>
            </div>
            <a class="subcol-video-modal__shop-btn" hidden>Shop Now</a>
          </div>
          <div class="subcol-video-modal__end-card" hidden>
            <p class="subcol-video-modal__end-text">You're all caught up — ready for more?</p>
            <a class="subcol-video-modal__end-btn" hidden>Watch more Reels</a>
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      video = modal.querySelector('.subcol-video-modal__video');
      closeBtn = modal.querySelector('.subcol-video-modal__close');
      viewMoreBtn = modal.querySelector('[data-action="view-more"]');
      muteBtn = modal.querySelector('[data-action="mute"]');
      muteIcon = muteBtn.querySelector('.subcol-video-modal__action-icon');
      shareBtn = modal.querySelector('[data-action="share"]');
      cardEl = modal.querySelector('.subcol-video-modal__card');
      titleEl = modal.querySelector('.subcol-video-modal__title');
      badgeEl = modal.querySelector('.subcol-video-modal__badge');
      shopBtn = modal.querySelector('.subcol-video-modal__shop-btn');
      flashEl = modal.querySelector('.subcol-video-modal__play-flash');
      videoWrap = modal.querySelector('.subcol-video-modal__video-wrap');
      navUpBtn = modal.querySelector('[data-nav="up"]');
      navDownBtn = modal.querySelector('[data-nav="down"]');
      endCardEl = modal.querySelector('.subcol-video-modal__end-card');
      endBtn = modal.querySelector('.subcol-video-modal__end-btn');

      closeBtn.addEventListener('click', close);
      viewMoreBtn.addEventListener('click', () => {
        if (viewMoreBtn.dataset.href) window.location.href = viewMoreBtn.dataset.href;
      });
      muteBtn.addEventListener('click', toggleMute);
      shareBtn.addEventListener('click', handleShare);
      navUpBtn.addEventListener('click', () => navigate(-1));
      navDownBtn.addEventListener('click', () => navigate(1));

      videoWrap.addEventListener('click', (e) => {
        if (suppressNextClick) { suppressNextClick = false; return; }
        togglePlay();
      });
      bindSwipe();

      document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowUp') navigate(-1);
        if (e.key === 'ArrowDown') navigate(1);
      });
    }

    let suppressNextClick = false;

    function bindSwipe() {
      let startX = 0;
      let startY = 0;
      let dragging = false;

      const start = (e) => {
        const point = e.touches ? e.touches[0] : e;
        startX = point.clientX;
        startY = point.clientY;
        dragging = true;
      };

      const end = (e) => {
        if (!dragging) return;
        dragging = false;
        const point = e.changedTouches ? e.changedTouches[0] : e;
        const diffX = startX - point.clientX;
        const diffY = startY - point.clientY;
        if (Math.abs(diffY) < 50 || Math.abs(diffY) < Math.abs(diffX)) return;
        suppressNextClick = true;
        navigate(diffY > 0 ? 1 : -1);
      };

      videoWrap.addEventListener('touchstart', start, { passive: true });
      videoWrap.addEventListener('touchend', end, { passive: true });
      videoWrap.addEventListener('mousedown', start);
      videoWrap.addEventListener('mouseup', end);
    }

    function navigate(direction) {
      if (showingEndCard) {
        if (direction < 0) {
          hideEndCard();
          open(triggerList[triggerIndex]);
        }
        return;
      }
      const nextIndex = triggerIndex + direction;
      if (nextIndex < 0) return;
      if (nextIndex >= triggerList.length) {
        showEndCard();
        return;
      }
      open(triggerList[nextIndex]);
    }

    function updateNavButtons() {
      navUpBtn.disabled = !showingEndCard && triggerIndex <= 0;
      navDownBtn.disabled = showingEndCard;
    }

    function showEndCard() {
      showingEndCard = true;
      video.pause();
      if (viewMoreBtn.dataset.href) {
        endBtn.href = viewMoreBtn.dataset.href;
        endBtn.hidden = false;
      } else {
        endBtn.hidden = true;
      }
      endCardEl.hidden = false;
      updateNavButtons();
    }

    function hideEndCard() {
      showingEndCard = false;
      endCardEl.hidden = true;
    }

    function toggleMute() {
      video.muted = !video.muted;
      muteIcon.innerHTML = video.muted ? ICON_MUTED : ICON_UNMUTED;
      muteBtn.querySelector('span:last-child').textContent = video.muted ? 'Unmute' : 'Mute';
    }

    function triggerFlash(iconHtml) {
      flashEl.innerHTML = iconHtml;
      flashEl.classList.remove('is-flashing');
      void flashEl.offsetWidth;
      flashEl.classList.add('is-flashing');
    }

    function togglePlay() {
      if (video.paused) {
        video.play().catch(() => {});
        triggerFlash(ICON_PLAY);
      } else {
        video.pause();
        triggerFlash(ICON_PAUSE);
      }
    }

    function handleShare() {
      const url = shopBtn.hidden ? window.location.href : shopBtn.href;
      const shareTitle = titleEl.textContent || document.title;
      if (navigator.share) {
        navigator.share({ title: shareTitle, url }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(showToast).catch(() => {});
      }
    }

    function showToast() {
      const t = document.createElement('div');
      t.textContent = 'Link copied!';
      t.style.cssText = 'position:fixed;bottom:90px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:#fff;padding:9px 20px;border-radius:22px;font-size:13px;z-index:9999;pointer-events:none;white-space:nowrap;';
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 2200);
    }

    function open(trigger) {
      if (!modal) build();
      hideEndCard();

      lastFocusedTrigger = trigger;

      const scope = trigger.closest('.carousel-track') || document;
      triggerList = Array.from(scope.querySelectorAll('.collection-item__video-trigger'));
      triggerIndex = triggerList.indexOf(trigger);
      updateNavButtons();

      const videoUrl = trigger.dataset.videoUrl;
      const title = trigger.dataset.title || '';
      const percentage = parseInt(trigger.dataset.percentage, 10) || 0;
      const discountPrefix = trigger.dataset.discountPrefix || 'none';
      const shopUrl = trigger.dataset.shopUrl || '';
      const reelsUrl = trigger.dataset.reelsUrl || '';

      if (!videoUrl) return;

      if (reelsUrl) {
        viewMoreBtn.dataset.href = reelsUrl;
        viewMoreBtn.hidden = false;
      } else {
        viewMoreBtn.hidden = true;
      }

      video.src = videoUrl;
      video.muted = false;
      muteIcon.innerHTML = ICON_UNMUTED;
      muteBtn.querySelector('span:last-child').textContent = 'Mute';

      if (title) {
        titleEl.textContent = title;
        titleEl.hidden = false;
      } else {
        titleEl.hidden = true;
      }
      cardEl.classList.toggle('subcol-video-modal__card--bare', !title);
      cardEl.hidden = !title && !shopUrl;

      if (percentage > 0) {
        if (discountPrefix === 'flat') {
          badgeEl.textContent = 'FLAT ' + percentage + '% OFF';
        } else if (discountPrefix === 'upto') {
          badgeEl.textContent = 'UPTO ' + percentage + '% OFF';
        } else {
          badgeEl.textContent = '-' + percentage + '%';
        }
        badgeEl.hidden = false;
      } else {
        badgeEl.hidden = true;
      }

      if (shopUrl) {
        shopBtn.href = shopUrl;
        shopBtn.hidden = false;
      } else {
        shopBtn.hidden = true;
      }

      modal.classList.add('is-open');
      document.body.classList.add('subcol-video-modal-open');
      document.dispatchEvent(new CustomEvent('subcol:video-modal-open'));
      video.play().catch(() => {});
    }

    function close() {
      if (!modal) return;
      modal.classList.remove('is-open');
      document.body.classList.remove('subcol-video-modal-open');
      document.dispatchEvent(new CustomEvent('subcol:video-modal-close'));
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (lastFocusedTrigger) {
        lastFocusedTrigger.focus({ preventScroll: true });
        lastFocusedTrigger = null;
      }
    }

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.collection-item__video-trigger');
      if (trigger) open(trigger);
    });

    return { open, close };
  })();
}